import ExcelJS from 'exceljs';

export const EXHIBITION_BULK_TEMPLATE_FILE_NAME = 'exhibition_bulk_template.xlsx';
export const EXHIBITION_BULK_TEMPLATE_MAX_ROWS = 1000;
export const EXHIBITION_BULK_TEMPLATE_SHEET_NAME = 'Exhibitions';
export const EXHIBITION_BULK_TEMPLATE_GUIDE_SHEET_NAME = 'Guide';

export const EXHIBITION_BULK_TEMPLATE_HEADERS = [
  'venueCode',
  'title',
  'titleEn',
  'startDate',
  'endDate',
  'officialUrl',
  'sourceUrl',
  'artists',
] as const;

const REQUIRED_HEADERS = new Set<string>([
  'venueCode',
  'title',
  'startDate',
  'endDate',
  'sourceUrl',
]);

const HEADER_FILL = 'FF27485D';
const REQUIRED_HEADER_FILL = 'FF173245';
const GUIDE_SECTION_FILL = 'FFDCEAF2';
const BORDER_COLOR = 'FFB9C6CE';

const COLUMN_WIDTHS: Record<(typeof EXHIBITION_BULK_TEMPLATE_HEADERS)[number], number> = {
  venueCode: 20,
  title: 32,
  titleEn: 32,
  startDate: 15,
  endDate: 15,
  officialUrl: 40,
  sourceUrl: 40,
  artists: 80,
};

const ARTISTS_EXAMPLE = JSON.stringify([{
  name: '홍길동',
  nameEn: 'Gildong Hong',
  instagramHandle: 'gildong.art',
  architraveHandle: 'gildong',
}]);

const applyHeader = (sheet: ExcelJS.Worksheet): void => {
  const row = sheet.addRow(EXHIBITION_BULK_TEMPLATE_HEADERS);
  row.height = 30;
  row.eachCell({ includeEmpty: true }, (cell) => {
    const required = REQUIRED_HEADERS.has(String(cell.value));
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: required ? REQUIRED_HEADER_FILL : HEADER_FILL },
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      bottom: { style: 'thin', color: { argb: BORDER_COLOR } },
    };
    cell.note = required ? 'Required' : 'Optional';
  });

  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  sheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: EXHIBITION_BULK_TEMPLATE_HEADERS.length },
  };
};

const createExhibitionsSheet = (workbook: ExcelJS.Workbook): void => {
  const sheet = workbook.addWorksheet(EXHIBITION_BULK_TEMPLATE_SHEET_NAME, {
    properties: { defaultRowHeight: 22 },
  });
  applyHeader(sheet);

  EXHIBITION_BULK_TEMPLATE_HEADERS.forEach((header, index) => {
    const column = sheet.getColumn(index + 1);
    column.width = COLUMN_WIDTHS[header];
    column.numFmt = '@';
    column.alignment = { vertical: 'top', wrapText: true };
  });
};

const createGuideSheet = (workbook: ExcelJS.Workbook): void => {
  const sheet = workbook.addWorksheet(EXHIBITION_BULK_TEMPLATE_GUIDE_SHEET_NAME, {
    properties: { defaultRowHeight: 22 },
  });
  sheet.columns = [
    { width: 24 },
    { width: 18 },
    { width: 90 },
  ];
  sheet.views = [{ state: 'frozen', ySplit: 2 }];

  sheet.mergeCells('A1:C1');
  const title = sheet.getCell('A1');
  title.value = 'Exhibition bulk upload guide';
  title.font = { bold: true, size: 16, color: { argb: 'FFFFFFFF' } };
  title.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: REQUIRED_HEADER_FILL },
  };
  title.alignment = { vertical: 'middle' };
  sheet.getRow(1).height = 34;

  const addSection = (name: string): void => {
    const row = sheet.addRow([name]);
    sheet.mergeCells(`A${row.number}:C${row.number}`);
    row.getCell(1).font = { bold: true, color: { argb: REQUIRED_HEADER_FILL } };
    row.getCell(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: GUIDE_SECTION_FILL },
    };
  };

  addSection('작성 방법');
  [
    ['시트/헤더', '', 'Exhibitions 시트명과 1행 헤더를 변경하지 마세요.'],
    ['행 수', '', `데이터는 최대 ${EXHIBITION_BULK_TEMPLATE_MAX_ROWS.toLocaleString()}행입니다.`],
    ['날짜', '', 'startDate와 endDate는 YYYY-MM-DD 형식으로 입력하세요.'],
    ['URL', '', 'sourceUrl은 필수, officialUrl은 선택입니다. http:// 또는 https://로 시작해야 합니다.'],
    ['Artist', '', 'artists는 JSON 배열입니다. name은 필수, nameEn은 선택입니다. 이름만 있으면 전시 크레딧으로만 저장됩니다.'],
  ].forEach((values) => sheet.addRow(values));

  addSection('필드');
  const fieldHeader = sheet.addRow(['Field', 'Required', 'Description']);
  const descriptions: Record<(typeof EXHIBITION_BULK_TEMPLATE_HEADERS)[number], string> = {
    venueCode: 'Venue 이름이 아닌 venueCode',
    title: '전시명',
    titleEn: '영문 전시명',
    startDate: '시작일 (YYYY-MM-DD)',
    endDate: '종료일 (YYYY-MM-DD)',
    officialUrl: '공식 URL (http/https)',
    sourceUrl: '출처 URL (http/https)',
    artists: 'JSON array: name, nameEn, instagramHandle, architraveHandle',
  };
  EXHIBITION_BULK_TEMPLATE_HEADERS.forEach((header) => {
    sheet.addRow([
      header,
      REQUIRED_HEADERS.has(header) ? 'YES' : '',
      descriptions[header],
    ]);
  });

  addSection('예시');
  const exampleHeader = sheet.addRow(['Field', 'Value', '']);
  const example: Record<(typeof EXHIBITION_BULK_TEMPLATE_HEADERS)[number], string> = {
    venueCode: 'MMCA-SEOUL',
    title: '전시명',
    titleEn: 'Exhibition',
    startDate: '2026-09-01',
    endDate: '2026-12-31',
    officialUrl: 'https://example.com/exhibition',
    sourceUrl: 'https://example.com/source',
    artists: ARTISTS_EXAMPLE,
  };
  EXHIBITION_BULK_TEMPLATE_HEADERS.forEach((header) => {
    sheet.addRow([header, example[header], '']);
  });

  [fieldHeader, exampleHeader].forEach((row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: HEADER_FILL },
      };
      cell.alignment = { vertical: 'middle' };
    });
  });
  sheet.eachRow((row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.alignment = { ...cell.alignment, vertical: 'top', wrapText: true };
    });
  });
};

export const buildExhibitionBulkTemplateWorkbook = (): ExcelJS.Workbook => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Architrave';
  workbook.title = 'Exhibition bulk upload template';
  workbook.subject = 'Exhibition bulk creation';

  createExhibitionsSheet(workbook);
  createGuideSheet(workbook);
  return workbook;
};

export const buildExhibitionBulkTemplateBlob = async (): Promise<Blob> => {
  const workbook = buildExhibitionBulkTemplateWorkbook();
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

export const downloadExhibitionBulkTemplate = async (): Promise<void> => {
  const blob = await buildExhibitionBulkTemplateBlob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = EXHIBITION_BULK_TEMPLATE_FILE_NAME;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};
